import React, { useState, useEffect } from 'react';
import { Bell, BellRinging } from '@phosphor-icons/react';
import { toast } from '../../hooks/use-toast';
import { pushAPI } from '../../api/api';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

export function PushNotifBanner() {
  const [state, setState] = useState('loading');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) { setState('unsupported'); return; }
    if (Notification.permission === 'denied') { setState('denied'); return; }
    (async () => {
      try { const res = await pushAPI.getStatus(); setState(res.data?.status?.subscribed ? 'subscribed' : 'prompt'); }
      catch { setState('prompt'); }
    })();
  }, []);

  const subscribe = async () => {
    setBusy(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') { setState('denied'); return; }
      const reg = await navigator.serviceWorker.register('/sw.js');
      const keyRes = await pushAPI.getVapidKey();
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(keyRes.data.publicKey) });
      const subJson = sub.toJSON();
      await pushAPI.subscribe({ endpoint: subJson.endpoint, keys: subJson.keys });
      setState('subscribed');
      toast({ title: 'Notifications enabled', description: 'You will receive alerts for new contractor interest and messages.' });
    } catch (err) { console.error('Push subscribe error:', err); toast({ title: 'Could not enable notifications', variant: 'destructive' }); }
    finally { setBusy(false); }
  };

  const testPush = async () => {
    setBusy(true);
    try { await pushAPI.sendTest(); toast({ title: 'Test notification sent' }); }
    catch { toast({ title: 'Test failed', variant: 'destructive' }); }
    finally { setBusy(false); }
  };

  if (state === 'loading' || state === 'unsupported' || state === 'denied') return null;

  if (state === 'subscribed') {
    return (
      <div className="flex items-center gap-3 p-3 bg-emerald-50 dark:bg-emerald-900/20 border-2 border-emerald-300 dark:border-emerald-700 mb-6" data-testid="push-enabled-banner">
        <BellRinging size={22} weight="bold" className="text-emerald-600 flex-shrink-0" />
        <p className="text-sm text-emerald-800 dark:text-emerald-300 font-medium flex-1">Push notifications are <strong>enabled</strong> — you'll be alerted when contractors show interest or message you.</p>
        <button onClick={testPush} disabled={busy} className="px-3 py-1 text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white border border-emerald-800 transition-colors disabled:opacity-50" data-testid="push-test-btn">{busy ? 'Sending...' : 'Test'}</button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 p-3 bg-[#2563EB]/10 border-2 border-[#2B4C6F] mb-6" data-testid="push-prompt-banner">
      <Bell size={22} weight="bold" className="text-[#2B4C6F] flex-shrink-0" />
      <p className="text-sm text-neutral-800 dark:text-neutral-200 font-medium flex-1">Get notified when contractors show interest in your projects or send you messages.</p>
      <button onClick={subscribe} disabled={busy} className="px-4 py-1.5 text-xs font-bold bg-[#2563EB] hover:bg-[#1D4ED8] text-[#0F172A] border-2 border-black shadow-[2px_2px_0px_#000] transition-all disabled:opacity-50 whitespace-nowrap" data-testid="push-enable-btn">{busy ? 'Enabling...' : 'Enable Notifications'}</button>
    </div>
  );
}
